/**
 * PremiumEmblem — emblème institutionnel animé pour GSF Arbitral AI
 * Balance augmentée : plateau gauche (IA explicable) + plateau droit (HITL souverain).
 * Respect prefers-reduced-motion (animations désactivées).
 */
interface PremiumEmblemProps {
  size?: number;
  className?: string;
}

export function PremiumEmblem({ size = 160, className }: PremiumEmblemProps) {
  return (
    <svg
      className={`premium-emblem ${className ?? ""}`}
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="GSF Arbitral AI — arbitrage institutionnel augmenté par l'IA"
      style={{ filter: "drop-shadow(0 12px 28px oklch(0.695 0.105 68 / 0.22))" }}
    >
      <defs>
        <linearGradient id="gsfEmblemGold" x1="20%" y1="0%" x2="80%" y2="100%">
          <stop offset="0%" stopColor="#E5C56B" />
          <stop offset="40%" stopColor="#C9A84C" />
          <stop offset="100%" stopColor="#9E7F1F" />
        </linearGradient>
        <linearGradient id="gsfEmblemMarine" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0D1B2A" />
          <stop offset="100%" stopColor="#163A5C" />
        </linearGradient>
      </defs>

      {/* Disque extérieur (rotation lente) */}
      <g className="premium-emblem-rotate">
        <circle cx="60" cy="60" r="56" fill="url(#gsfEmblemMarine)" stroke="url(#gsfEmblemGold)" strokeWidth="1.5" />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="#C9A84C"
          strokeWidth="0.8"
          opacity="0.6"
          className="premium-emblem-dash"
        />
        <circle cx="60" cy="60" r="48" fill="#0F2F4F" />
      </g>

      {/* Balance : pilier + fléau + socle */}
      <line x1="60" y1="34" x2="60" y2="74" stroke="url(#gsfEmblemGold)" strokeWidth="3.5" strokeLinecap="round" />
      <path d="M44 74 L76 74 L66 82 L54 82 Z" fill="url(#gsfEmblemGold)" />
      <path d="M54 82 L66 82 L62 86 L58 86 Z" fill="#A8811A" />
      <line x1="30" y1="34" x2="90" y2="34" stroke="url(#gsfEmblemGold)" strokeWidth="3.5" strokeLinecap="round" />

      {/* Plateau GAUCHE — IA explicable (pulsation) */}
      <g className="premium-emblem-pulse">
        <line x1="30" y1="34" x2="30" y2="48" stroke="#D4AF37" strokeWidth="2.5" />
        <circle cx="30" cy="54" r="9" fill="none" stroke="url(#gsfEmblemGold)" strokeWidth="2.2" />
        <rect x="25" y="50" width="10" height="8" rx="2" fill="#1F4A6F" />
        <circle cx="27.5" cy="52.5" r="1.2" fill="#AAD0F5" />
        <circle cx="32.5" cy="52.5" r="1.2" fill="#AAD0F5" />
        <line x1="27.5" y1="54" x2="32.5" y2="54" stroke="#7FB4D4" strokeWidth="1" />
        <path
          d="M24 61 L28 64 L36 57"
          stroke="#7BC67E"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>

      {/* Plateau DROIT — HITL souverain */}
      <g>
        <line x1="90" y1="34" x2="90" y2="48" stroke="#D4AF37" strokeWidth="2.5" />
        <circle cx="90" cy="54" r="9" fill="none" stroke="url(#gsfEmblemGold)" strokeWidth="2.2" />
        <path
          d="M87.5 49 L87.5 52 C87.5 53 88.5 54 89.8 54 L90.2 54 C91.5 54 92.5 53 92.5 52 L92.5 49"
          fill="none"
          stroke="#F0EAD6"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <circle cx="90" cy="46.5" r="3" fill="none" stroke="#F0EAD6" strokeWidth="1.6" />
        <path
          d="M85 60 L88 63 L95 56"
          stroke="#E6B422"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>

      {/* Ancrage central — sceau numérique */}
      <circle cx="60" cy="44" r="5.5" fill="#163A5C" stroke="#C9A84C" strokeWidth="1.8" />
      <path d="M57.5 44 L60 41.5 L62.5 44 L60 46.5 Z" fill="#D4AF37" opacity="0.9" />

      {/* Marqueurs périphériques — espace OHADA */}
      <path d="M14 48 L18 44 L22 48 L18 54 Z" fill="#2E7D32" opacity="0.75" />
      <path d="M98 72 L102 68 L106 72 L102 78 Z" fill="#1E405E" stroke="#C9A84C" strokeWidth="1" opacity="0.9" />
      <circle cx="72" cy="22" r="1.2" fill="#C9A84C" opacity="0.7" />
      <circle cx="48" cy="22" r="1.2" fill="#C9A84C" opacity="0.7" />
    </svg>
  );
}
